import React from "react";
import { RouteComponentProps } from "react-router-dom";
import "./Course.css";
import "../../App.css";
import SingleCourseElement from "../../components/SingleCourseElement";
import BlockCourseElement from "../../components/BlockCourseElement";
import {
  dateToFullStr,
  firebaseDateToDateObj,
  getSubscriptionPriceString,
  objectToArray,
  priceToStr,
} from "../../functions/utils";
import { fetchRetry } from "../../functions/request";
import { MainContext } from "../../contexts/MainContext";
import JoinBranchModal from "../../components/modals/JoinBranchModal";
import RequestedBranchModal from "../../components/modals/RequestedBranchModal";
import InfinityCourseElement from "../../components/InfinityCourseElement";
import TestMode from "../../components/TestMode";
import Title from "../../components/Title";
import SearchInput from "../../components/SearchInput";
import Tag from "../../components/Tag";
import ReqMemAppMessage from "../../components/ReqMemAppMessage";
import SubscriptionElement from "../../components/SubscriptionElement";
import qs from "qs";
import InfiniteScroll from "react-infinite-scroll-component";
import CourseElementSkeleton from "../../components/CourseElementSkeleton";
import debounce from "lodash.debounce";
import EnterMembershipModal from "../../components/modals/EnterMembershipModal";
import { apiRequest } from "../../functions/requestMulti";
import CourseViewSwitch from "../../components/CourseViewSwitch";
import Timetable, { TimetableSnapshot } from "../../components/Timetable";
import { isTimetableEnabled } from "../../functions/timetable";
import {
  ListCacheEntry,
  getScrollContainer,
  restoreListState,
  restoreScrollPosition,
  saveListState,
} from "../../functions/listStateCache";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
    branchId: string;
  }>;

export default class Course extends React.Component<
  ComposedProps,
  {
    clubId: string;
    branchId: string;
    branchLoaded: boolean;
    elementsLoaded: boolean;
    branch: any;
    elements: Array<any>;
    showJoinModal: boolean;
    joinModalLoading: boolean;
    joinModalMsg: null | string;
    joinModalType: "error" | "success";
    showRequestedModal: boolean;
    requestedModalLoading: boolean;
    requestedModalMsg: null | string;
    requestedModalType: "error" | "success";
    courseSearch: string;
    selectedTag: string;
    subscriptions: Array<any>;
    neededRoles: Array<string>;
    totalLoaded: boolean;
    redirect: string | null;
    page: number;
    hasMore: boolean;
    batchSize: number;
    initialLoad: boolean;
    // 1 = Liste, 2 = Stundenplan
    view: number;
    timetableState: TimetableSnapshot | null;
  }
> {
  static contextType = MainContext;

  private cacheKey: string;
  private restored: ListCacheEntry | null;
  private leavingToDetail = false;
  private cancelScroll: (() => void) | null = null;
  private mounted = false;
  private restoringPosition = false;
  private rootRef = React.createRef<HTMLDivElement>();
  private timetableRef = React.createRef<Timetable>();
  // released, or previewed with ?calendar=true
  private showTimetable = isTimetableEnabled();

  constructor(props: ComposedProps) {
    super(props);
    this.cacheKey = props.match.url;
    this.restored = restoreListState(this.cacheKey);
    this.state = {
      clubId: this.props.match.params.clubId,
      branchId: this.props.match.params.branchId,
      branchLoaded: false,
      elementsLoaded: false,
      branch: {},
      elements: [],
      showJoinModal: false,
      joinModalLoading: false,
      joinModalMsg: null,
      joinModalType: "error",
      showRequestedModal: false,
      requestedModalLoading: false,
      requestedModalMsg: null,
      requestedModalType: "error",
      courseSearch: "",
      selectedTag: "",
      subscriptions: [],
      totalLoaded: false,
      page: 0,
      hasMore: true,
      batchSize: 10,
      initialLoad: false,
      timetableState: null,
      // Returning from a course's detail page: render every page the user had
      // already loaded, plus their search and tag filter.
      ...(this.restored ? this.restored.state : {}),
      view: this.initialView(),
      neededRoles: (
        qs.parse(this.props.location.search, {
          ignoreQueryPrefix: true,
        }).neededRoles + "" || ""
      ).split(","),
      redirect:
        qs.parse(this.props.location.search, {
          ignoreQueryPrefix: true,
        }).redirect + "" || null,
    };
  }

  componentDidMount = async () => {
    this.mounted = true;
    if (this.showTimetable) this.syncViewToUrl(this.state.view);
    if (this.restored) {
      this.restorePosition();
      // Silent refresh of the whole loaded window - elementsLoaded stays true,
      // so no skeletons and no scroll jump.
      this.revalidateWindow();
      this.requestBranch();
      return;
    }
    await Promise.all([this.requestBranch(), this.requestElements()]).then(
      this.handleData
    );
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.cancelScroll) this.cancelScroll();
    this.debounceRequest.cancel();
    if (this.leavingToDetail) {
      saveListState(
        this.cacheKey,
        {
          elements: this.state.elements,
          page: this.state.page,
          hasMore: this.state.hasMore,
          courseSearch: this.state.courseSearch,
          selectedTag: this.state.selectedTag,
          branch: this.state.branch,
          elementsLoaded: true,
          branchLoaded: true,
          totalLoaded: true,
          initialLoad: true,
          view: this.state.view,
          timetableState: this.currentTimetableState(),
        },
        this.rootRef.current
      );
    }
  };

  /**
   * A back link may carry a `#courseId`. When it names a card that is actually
   * on screen, honour it - it is more precise than the saved offset. Otherwise
   * fall back to the saved scroll position.
   */
  restorePosition = () => {
    this.restoringPosition = true;
    const settled = () => {
      this.restoringPosition = false;
    };
    const hashId = window.location.hash.slice(1);
    const target = hashId ? document.getElementById(hashId) : null;
    if (target) {
      this.scrollToCard(target);
      requestAnimationFrame(settled);
      return;
    }
    if (!this.restored) {
      settled();
      return;
    }
    this.cancelScroll = restoreScrollPosition(
      this.restored,
      this.rootRef.current,
      30, // cards grow as their images decode
      settled
    );
  };

  scrollToCard = (target: HTMLElement) => {
    const container = getScrollContainer(this.rootRef.current);
    if (!container) {
      target.scrollIntoView();
      return;
    }
    // `.br-inner-container` has `scroll-behavior: smooth`, which would animate
    // the jump; an inline style beats the stylesheet rule.
    const prev = container.style.scrollBehavior;
    container.style.scrollBehavior = "auto";
    target.scrollIntoView();
    container.style.scrollBehavior = prev;
  };

  /**
   * Re-reads every page the user had already loaded and replaces the list, so
   * changes made elsewhere show up after a back-navigation.
   *
   * One request per page rather than one big one: the v2 API caps hitsPerPage
   * at 20 (Zod `.max(20)` in elementSevice), and exceeding it fails the parse
   * and returns a 500.
   */
  revalidateWindow = async () => {
    const pages = [];
    for (let i = 0; i <= this.state.page; i++) pages.push(i);
    const results = await Promise.all(
      pages.map((page) => this.fetchElementPage(page))
    ).catch(this.handleError);
    if (!results || !this.mounted) return;

    const elements: Array<any> = [];
    const seen: { [id: string]: boolean } = {};
    results.forEach((pageElements: any) => {
      (pageElements || []).forEach((element: any) => {
        // Offset paging can shift when items are added or removed between
        // visits, so the same element can come back on two pages.
        if (element && !seen[element.id]) {
          seen[element.id] = true;
          elements.push(element);
        }
      });
    });

    const lastPage = results[results.length - 1];
    this.setState({
      // Same enrichment handleData applies, otherwise the refresh would strip
      // memLink off every card.
      elements: this.withMemLinks(elements),
      hasMore: !!lastPage && lastPage.length >= this.state.batchSize,
      totalLoaded: true,
    });
  };

  fetchElementPage = async (page: number) => {
    const { data } = await apiRequest(
      "api/elements/branch",
      "GET",
      this.elementParams(page)
    );
    return data.success ? data.data : null;
  };

  elementParams = (page: number) => ({
    targetUserId: this.context.selectedUserId,
    clubId: this.state.clubId,
    branchId: this.state.branchId,
    query: this.state.courseSearch,
    page,
    hitsPerPage: this.state.batchSize,
    showDeleted: false,
    showPast: false,
    tag:
      this.state.selectedTag !== "subscriptions" ? this.state.selectedTag : "",
    onlySubscriptions: this.state.selectedTag === "subscriptions",
  });

  requestBranch = async () => {
    await fetchRetry(
      "getBranchFromIdF",
      {
        clubId: this.state.clubId,
        branchId: this.state.branchId,
        targetUserId: this.context.selectedUserId,
      },
      1,
      5
    )
      .then(this.handleBranch)
      .catch(this.handleError);
  };

  getNextPage = () => {
    // The restore pass writes scrollTop programmatically, which fires scroll
    // events. Without this guard InfiniteScroll would append a page - and
    // flash skeletons - before the restored list has settled.
    if (this.restoringPosition) return;
    this.setState(
      { page: this.state.page + 1, elementsLoaded: false },
      this.requestElements
    );
  };

  handleBranch = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ branchLoaded: true });
    if (data.success) {
      const branch = data.data;
      this.setState({
        branch: branch,
      });
    }
  };

  requestElements = async () => {
    await apiRequest(
      "api/elements/branch",
      "GET",
      this.elementParams(this.state.page)
    )
      .then(this.handleElementsSuccess)
      .catch(this.handleError);
    // await fetchRetry(
    //   "elements",
    //   {
    //     targetUserId: this.context.selectedUserId,
    //     clubId: this.state.clubId,
    //     branchId: this.state.branchId,
    //     query: this.state.courseSearch,
    //     page: this.state.page,
    //     hitsPerPage: this.state.batchSize,
    //     showDeleted: false,
    //     showPast: false,
    //     tag:
    //       this.state.selectedTag !== "subscriptions"
    //         ? this.state.selectedTag
    //         : "",
    //     onlySubscriptions: this.state.selectedTag === "subscriptions",
    //   },
    //   1,
    //   5
    // )
    //   .then(this.handleElementsSuccess)
    //   .catch(this.handleError);
  };

  handleError = (err: any) => {
    console.error(err);
  };

  handleElementsSuccess = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ elementsLoaded: true });
    if (data.success) {
      const elements = this.state.elements.concat(data.data);
      this.setState(
        {
          elements,
          hasMore: data.data.length >= this.state.batchSize,
        },
        () => {
          if (!this.state.initialLoad) {
            // `location.hash` rather than slicing the full href - the old
            // version handed getElementById the entire URL whenever there was
            // no "#" at all.
            const highlighedCourseId = window.location.hash.slice(1);
            const element = highlighedCourseId
              ? document.getElementById(highlighedCourseId)
              : null;
            if (element) this.scrollToCard(element);
            this.setState({ initialLoad: true });
          }
        }
      );
    }
  };

  handleData = () => {
    if (!this.mounted) return;
    this.setState({
      elements: this.withMemLinks(this.state.elements),
      totalLoaded: true,
    });
  };

  withMemLinks = (elements: Array<any>) => {
    return elements.map((course: any) => {
      let totalSubscriptionArr: Array<string> = [];
      if (course.userAllowedToBook) {
        return {
          ...course,
          memLink: null,
        };
      }
      const memAppRoleArr =
        this.state.branch?.clubSettings?.memAppRoleArr || [];
      for (const allowedRole of course.allowedRoles) {
        if (memAppRoleArr.includes(allowedRole)) {
          return {
            ...course,
            memLink:
              this.state.branch?.clubSettings?.requestMembershipUrl || null,
          };
        }
        const branchRoles = this.state.branch?.roles || [];
        const subscriptionArr = Object.keys(branchRoles).includes(allowedRole)
          ? branchRoles[allowedRole]?.subscriptions
          : [];
        totalSubscriptionArr = subscriptionArr.concat(totalSubscriptionArr);
      }
      if (totalSubscriptionArr.length === 0) {
        return {
          ...course,
          memLink: null,
        };
      }
      const subscriptionLink = `https://${
        this.context.club.nameIdentifier
      }.getbookable.de/club/${this.state.clubId}/branch/${
        this.state.branchId
      }/course?showSub=true&neededRoles=${totalSubscriptionArr.join(",")}`;
      return {
        ...course,
        memLink: subscriptionLink,
      };
    });
  };

  handleChangeCourseSearch = (val: string) => {
    this.setState({ courseSearch: val }, this.debounceRequest);
  };

  debounceRequest = debounce(() => {
    this.newRequest();
  }, 500);

  newRequest = () => {
    this.setState(
      { elements: [], page: 0, elementsLoaded: false, hasMore: true },
      this.requestElements
    );
  };

  handleShowJoinModal = () => {
    this.setState({ showJoinModal: true });
  };

  handleBranchJoinModalClose = () => {
    this.requestBranch();
    this.setState({ showJoinModal: false, joinModalMsg: null });
  };

  handleShowRequestedModal = () => {
    this.setState({ showRequestedModal: true });
  };

  handleBranchRequestedModalClose = () => {
    this.setState({ showRequestedModal: false, requestedModalMsg: null });
  };

  /**
   * The Stundenplan is the default, the list is `?view=liste`. An explicit
   * `?view=` wins (old `?view=stundenplan` links keep working); without one -
   * the course page's own "Zurück zur Kursauswahl" link drops the query - the
   * view restored from the list cache does.
   */
  initialView = (): number => {
    if (!this.showTimetable) return 1;
    const param = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    }).view;
    if (param === "liste") return 1;
    if (param === "stundenplan") return 2;
    if (this.restored && this.restored.state.view === 1) return 1;
    return 2;
  };

  handleViewChange = (view: number) => {
    // Leaving the Stundenplan keeps its week, day and data, so switching back
    // shows it without reloading.
    this.setState({ view, timetableState: this.currentTimetableState() });
    this.syncViewToUrl(view);
  };

  /**
   * The Stundenplan's state as it is on screen now. Called while it is still
   * mounted - also from componentWillUnmount, which React runs on this screen
   * before it unmounts the children.
   */
  currentTimetableState = (): TimetableSnapshot | null => {
    if (this.state.view === 2 && this.timetableRef.current) {
      return this.timetableRef.current.getSnapshot();
    }
    return this.state.timetableState;
  };

  /**
   * The view lives in the URL so that "Zurück" from a course lands on it and
   * the Stundenplan can be linked directly. replace, not push, like the tab
   * switch on the participants screen.
   */
  syncViewToUrl = (view: number) => {
    const params: any = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });
    // the Stundenplan is the default and needs no parameter
    const wanted = view === 1 ? "liste" : undefined;
    if (params.view === wanted) return;
    if (wanted) {
      params.view = wanted;
    } else {
      delete params.view;
    }
    this.props.history.replace({
      search: qs.stringify(params),
      hash: this.props.location.hash,
    });
  };

  handleTagClicked = (tagId: string) => {
    if (tagId === this.state.selectedTag) {
      this.setState({ selectedTag: "" }, this.newRequest);
    } else {
      this.setState({ selectedTag: tagId }, this.newRequest);
    }
  };

  /**
   * The cards navigate through their own <Link>s, so there is no single
   * redirect handler to hook. A capture-phase listener on the list container
   * catches all of them without touching the card components - and ignores
   * sidebar navigation, which happens outside this subtree.
   */
  handleListClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target && target.closest && target.closest("a")) {
      this.leavingToDetail = true;
    }
  };

  render() {
    const tags = this.state.branch?.tags ? this.state.branch.tags : {};
    const tagArr = objectToArray(tags);
    tagArr.sort((a: any, b: any) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );

    const testMode = this.state.branch?.billing?.mode === "test";

    const club = this.context.club;
    const userRoles: string[] = club?.user?.roles || [];
    const clubRoleIds: string[] = Object.keys(club?.roles || {}) || [];
    const doesUserHasClubRole: boolean = clubRoleIds.some((role) =>
      userRoles.includes(role)
    );

    return (
      <>
        <EnterMembershipModal
          clubId={this.state.clubId}
          show={this.state.showJoinModal}
          clubName={this.context.club?.name}
          membershipName={this.context.club?.text?.membershipNumberText}
          handleClose={this.handleBranchJoinModalClose}
          useMembershipnumber={this.context.club?.settings?.useMembershipNumber}
          askForMembershipnumber={
            this.context.club?.settings?.askForMembershipNumber
          }
          useBirthDate={this.context.club?.settings?.bBirthDate}
          useAddress={this.context.club?.settings?.bAddress}
          usePhoneNumber={this.context.club?.settings?.bPhoneNumber}
          alreadyMemberBtnText={this.context.club?.text?.alreadyMemberBtnText}
        />
        <RequestedBranchModal
          clubId={this.state.clubId}
          branchId={this.state.branchId}
          show={this.state.showRequestedModal}
          clubName={this.state.branch?.club?.name}
          branchName={this.state.branch?.name}
          handleClose={this.handleBranchRequestedModalClose}
          refresh={this.requestBranch}
        />
        <div
          className="subscreen-main-container"
          ref={this.rootRef}
          onClickCapture={this.handleListClick}
        >
          <div className="subscreen-box-container">
            <div className="course-title-row">
              <Title title="Kurse" />
              {this.showTimetable && (
                <CourseViewSwitch
                  view={this.state.view}
                  onChange={this.handleViewChange}
                />
              )}
            </div>
            <TestMode active={testMode} />
            <ReqMemAppMessage
              show={
                this.state.branch?.settings?.showRequestWindow &&
                !doesUserHasClubRole
              }
              alreadyMemberBtnText={
                this.state.branch?.clubSettings?.alreadyMemberBtnText
              }
              alreadyMemberText={
                this.state.branch?.clubSettings?.alreadyMemberText
              }
              clubId={this.state.clubId}
              handleShowJoinModal={this.handleShowJoinModal}
              handleShowRequestedModal={this.handleShowRequestedModal}
              hasRequested={this.state.branch?.user?.requested}
              requestMembershipBtnText={
                this.state.branch?.clubSettings?.requestMembershipBtnText
              }
              requestMembershipText={
                this.state.branch?.clubSettings?.requestMembershipText
              }
              requestMembershipUrl={
                this.state.branch?.clubSettings?.requestMembershipUrl
              }
            />
            {this.state.view === 2 && (
              <Timetable
                ref={this.timetableRef}
                scope="branch"
                clubId={this.state.clubId}
                branchId={this.state.branchId}
                initialState={this.state.timetableState}
              />
            )}
            {this.state.view === 1 && (
              <>
                <SearchInput
                  name="course-search-input"
                  placeholder="Kurssuche"
                  value={this.state.courseSearch}
                  onChange={this.handleChangeCourseSearch}
                  className="no-margin"
                />
                <div className="tag-rating-container">
                  {this.state.branch?.settings?.useSubscriptions && (
                    <Tag
                      item={"Mitgliedschaften"}
                      selected={this.state.selectedTag === "subscriptions"}
                      color={"#c31924"}
                      onClick={() => {
                        this.handleTagClicked("subscriptions");
                      }}
                    />
                  )}
                  {tagArr.map((currTag: any) => (
                    <>
                      <Tag
                        item={currTag.name}
                        selected={currTag.id === this.state.selectedTag}
                        color={"#5DADE2"}
                        onClick={() => {
                          this.handleTagClicked(currTag.id);
                        }}
                      />
                    </>
                  ))}
                </div>

                <>
                  {this.state.elements.length === 0 &&
                    this.state.elementsLoaded && (
                      <>
                        {this.state.courseSearch === "" &&
                        !this.state.selectedTag ? (
                          <div className="no-courses-container">
                            In diesem Bereich gibt es aktuell keine Kurse.
                          </div>
                        ) : (
                          <div className="no-courses-container">
                            Mit diesem Filter gibt es aktuell keine Kurse.
                          </div>
                        )}
                      </>
                    )}
                  <InfiniteScroll
                    dataLength={this.state.elements.length}
                    next={this.getNextPage}
                    hasMore={this.state.hasMore}
                    loader={<></>}
                    scrollableTarget="scroll-container"
                  >
                    {this.state.elements.map((elem: any) => (
                      <>
                        {elem.courseType === "subscription" && (
                          <SubscriptionElement
                            clubId={this.state.clubId}
                            branchId={this.state.branchId}
                            subscriptionId={elem.id}
                            title={elem.title}
                            description={elem.description}
                            price={getSubscriptionPriceString(
                              elem.price,
                              elem.admissionFee,
                              elem.billingPeriod
                            )}
                            imgUrl={elem.imgLowRes}
                            key={elem.id}
                            userAllowedToBook={elem.userAllowedToBook}
                            minAge={elem.minAge}
                            maxAge={elem.maxAge}
                            highlighted={this.state.neededRoles.includes(elem.id)}
                            requestMembershipUrl={
                              this.state.branch?.clubSettings?.requestMembershipUrl
                            }
                            redirect={this.state.redirect}
                          />
                        )}
                        {elem.courseType === "single-course" && (
                          <SingleCourseElement
                            clubId={this.state.clubId}
                            branchId={this.state.branchId}
                            courseId={elem.id}
                            title={elem.title}
                            description={elem.description}
                            startTime={elem.startTime}
                            endTime={elem.endTime}
                            where={elem.location.name}
                            freePlaces={
                              testMode ? elem.freePlacesTest : elem.freePlaces
                            }
                            price={priceToStr(elem.price)}
                            imgUrl={elem.imgLowRes}
                            key={elem.id}
                            bookableFrom={firebaseDateToDateObj(elem.bookableDate)}
                            userAllowedToBook={elem.userAllowedToBook}
                            minAge={elem.minAge}
                            maxAge={elem.maxAge}
                            requestMembershipUrl={
                              this.state.branch?.clubSettings?.requestMembershipUrl
                            }
                            memLink={elem.memLink}
                          />
                        )}
                        {elem.courseType === "block-course" && (
                          <BlockCourseElement
                            clubId={this.state.clubId}
                            branchId={this.state.branchId}
                            courseId={elem.id}
                            title={elem.title}
                            description={elem.description}
                            when={dateToFullStr(
                              firebaseDateToDateObj(elem.startTime)
                            )}
                            courses={elem.courses}
                            freePlaces={
                              testMode ? elem.freePlacesTest : elem.freePlaces
                            }
                            price={priceToStr(elem.price)}
                            imgUrl={elem.imgLowRes}
                            key={elem.id}
                            bookableFrom={firebaseDateToDateObj(elem.bookableDate)}
                            userAllowedToBook={elem.userAllowedToBook}
                            minAge={elem.minAge}
                            maxAge={elem.maxAge}
                            requestMembershipUrl={
                              this.state.branch?.clubSettings?.requestMembershipUrl
                            }
                            memLink={elem.memLink}
                          />
                        )}
                        {elem.courseType === "infinity-course" && (
                          <InfinityCourseElement
                            clubId={this.state.clubId}
                            branchId={this.state.branchId}
                            courseId={elem.id}
                            title={elem.title}
                            description={elem.description}
                            where={elem.location?.name}
                            time={elem.time}
                            duration={elem.duration}
                            weekDay={elem.weekDay}
                            freePlaces={
                              testMode ? elem.freePlacesTest : elem.freePlaces
                            }
                            price={priceToStr(elem.price)}
                            imgUrl={elem.imgLowRes}
                            key={elem.id}
                            bookableFrom={firebaseDateToDateObj(elem.bookableDate)}
                            userAllowedToBook={elem.userAllowedToBook}
                            minAge={elem.minAge}
                            maxAge={elem.maxAge}
                            requestMembershipUrl={
                              this.state.branch?.clubSettings?.requestMembershipUrl
                            }
                            memLink={elem.memLink}
                          />
                        )}
                      </>
                    ))}
                  </InfiniteScroll>
                  {!this.state.elementsLoaded && (
                    <>
                      <CourseElementSkeleton />
                      <CourseElementSkeleton />
                      <CourseElementSkeleton />
                      <CourseElementSkeleton />
                    </>
                  )}
                  {this.state.elementsLoaded && this.state.hasMore && (
                    <div className="load-more-container" onClick={this.getNextPage}>
                      Mehr laden
                    </div>
                  )}
                </>
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}
